// @flow

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { trackEvent, ENTER_PAGE } from '../../../services/mixpanel';
import { v4 as uuidv4 } from 'uuid';
import { Spinner } from '@blueprintjs/core';
import Layout from '../../layouts/default-logged-in';
import { Panel, PanelBody } from '../../panel';
import { ChatWindow, PromptFooter, usePromptChatController, usePromptChatState, OrganisationContext } from '../../ai-prompt-management';
import AIChatWrapper from '../../ai-chat/chat/AIChatWrapper';
import TopBar from '../../ai-chat/top-bar';
import ChatTabs from './chat-tabs';
import { Mobile } from '../../layouts/devices-sizes';
import { Row, TopBar as TopBarLayout } from '../summary/layout';
import { errorToast } from '../../../utils/toaster';
import { MAX_PROMPT_CHARS, PLACEHOLDER_DEFAULT } from '../../ai-chat/chat/constants';
import { aiChat } from '../../../services/api';
import { fetchChatThreads, updateChatThread, refreshThreadsForTitle } from '../../../actions/chatThreads';
import * as ROUTES from '../../../constants/routes';
import * as Selectors from '../../../selectors';
import { ORG_HAS_STAFF_DETECTION, DISABLE_AGE_GENDER } from '../../../constants/features';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StretchPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  max-width: none;
`;

const BodyFlex = styled(PanelBody)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-width: none;
  /* Match /my-reports bottom spacing by avoiding extra bottom padding here. */
  padding: 0.5rem 0 0;
  background: var(--main-background);
`;

const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
`;

const ChatAreaWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const GENERIC_ERROR_TOAST_MESSAGE = 'Something went wrong, please try again.';
const HYDRATION_ERROR_MESSAGE = 'We could not retrieve your conversation';

// Inner component that uses chat hooks (must be inside AIChatWrapper)
const ChatWithControls = ({ 
  inputRef, 
  controllerResetRef, 
  controllerHydrateRef,
  onGetThreadId,
  isHydrating,
  hydrationError,
  initialPrompt,
  onConsumeInitialPrompt,
}) => {
  const [prompt, setPrompt] = useState('');
  const controller = usePromptChatController();
  const state = usePromptChatState();
  const lastErrorRef = useRef(null);
  const initialPromptSentRef = useRef(false);
  
  // Expose controller methods to parent via refs
  useEffect(() => {
    if (controllerResetRef) {
      controllerResetRef.current = controller.reset;
    }
    if (controllerHydrateRef) {
      controllerHydrateRef.current = controller.hydrate;
    }
  }, [controller.reset, controller.hydrate, controllerResetRef, controllerHydrateRef]);
  
  // Expose threadId (sessionId in generic component) to parent
  useEffect(() => {
    if (onGetThreadId && state.sessionId) {
      onGetThreadId(state.sessionId);
    }
  }, [state.sessionId, onGetThreadId]);
  
  const promptTooLong = useMemo(
    () => String(prompt || '').length > (state.maxPromptChars || MAX_PROMPT_CHARS),
    [prompt, state.maxPromptChars]
  );

  // Surface errors via toast
  useEffect(() => {
    const err = state && state.error;
    const message = err && err.message;
    if (!message) return;
    if (lastErrorRef.current === err) return;
    lastErrorRef.current = err;
    errorToast({ message: GENERIC_ERROR_TOAST_MESSAGE });
  }, [state && state.error]);

  const handleSend = useCallback(async () => {
    if (!prompt.trim()) return;
    await controller.send(prompt);
    setPrompt('');
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [prompt, controller, inputRef]);

  // Consume an initial prompt passed via router state (Home shortcut)
  useEffect(() => {
    const text = String(initialPrompt || '').trim();
    if (!text) return;
    if (initialPromptSentRef.current) return;

    // Don’t auto-send while hydrating, streaming, locked, or on hydration error
    if (isHydrating || hydrationError || state.isStreaming || state.promptLocked) return;

    // Respect max prompt length; let user edit if too long
    const maxChars = state.maxPromptChars || MAX_PROMPT_CHARS;
    if (text.length > maxChars) {
      setPrompt(text);
      initialPromptSentRef.current = true;
      if (onConsumeInitialPrompt) onConsumeInitialPrompt();
      return;
    }

    // Mark sent before awaiting to avoid duplicate sends on rerender
    initialPromptSentRef.current = true;
    (async () => {
      try {
        await controller.send(text);
        setPrompt('');
        if (inputRef && inputRef.current) inputRef.current.focus();
      } finally {
        if (onConsumeInitialPrompt) onConsumeInitialPrompt();
      }
    })();
  }, [
    controller,
    hydrationError,
    initialPrompt,
    inputRef,
    isHydrating,
    onConsumeInitialPrompt,
    state.isStreaming,
    state.maxPromptChars,
    state.promptLocked,
  ]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Compute disabled state - disable during hydration or on hydration error
  const inputsDisabled = isHydrating || hydrationError || state.isStreaming || promptTooLong || state.promptLocked;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, width: '100%', gap: '0.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, width: '100%' }}>
        <ChatWindow />
      </div>
      <div style={{ flex: '0 0 auto', width: '100%' }}>
        <PromptFooter
          inputRef={inputRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          isLoading={state.isStreaming}
          onSend={handleSend}
          placeholder={PLACEHOLDER_DEFAULT}
          sendDisabled={inputsDisabled}
          notice={
            promptTooLong
              ? `Prompt is too long (${String(prompt || '').length}/${state.maxPromptChars || MAX_PROMPT_CHARS}). Please shorten your request.`
              : undefined
          }
        />
      </div>
    </div>
  );
};

const AIChat = ({ dispatch, history: propsHistory, chatThreads, organisationName, staffDetectionDisabled, demographicsDisabled, availableMetricGroups }) => {
  const { threadId: urlThreadId } = useParams();
  const routerHistory = useHistory();
  const history = propsHistory || routerHistory;
  const location = useLocation();
  const initialPrompt = location && location.state && location.state.initialPrompt;
  const consumeInitialPrompt = useCallback(() => {
    if (!location || !location.state || !location.state.initialPrompt) return;
    // Clear initialPrompt so it won't resend on back/forward or rerender
    history.replace({
      ...location,
      state: {
        ...location.state,
        initialPrompt: undefined,
      },
    });
  }, [history, location]);
  
  const [currentThreadId, setCurrentThreadId] = useState(() => urlThreadId || uuidv4());
  // IMPORTANT: Start hydrating if we have a threadId in URL on initial mount
  const [isHydrating, setIsHydrating] = useState(!!urlThreadId);
  const [hydrationError, setHydrationError] = useState(false);
  const [suppressIntro, setSuppressIntro] = useState(!!urlThreadId);
  
  const inputRef = useRef(null);
  const controllerResetRef = useRef(null);
  const controllerHydrateRef = useRef(null);
  // Initialize ref with current URL threadId so initial fetch works
  const prevUrlThreadIdRef = useRef(null);

  // Track page view on mount
  useEffect(() => {
    trackEvent(ENTER_PAGE, 'ASK_AURA');
  }, []);

  // Focus input on mount (only if not hydrating)
  useEffect(() => {
    if (inputRef.current && !isHydrating) inputRef.current.focus();
  }, [isHydrating]);

  // Fetch thread list on mount
  useEffect(() => {
    dispatch(fetchChatThreads());
  }, [dispatch]);

  // Handle URL thread ID changes (navigation to /ai-chat/:threadId)
  useEffect(() => {
    const fetchThreadHistory = async (threadId) => {
      setIsHydrating(true);
      setHydrationError(false);
      setSuppressIntro(true);
      
      try {
        const response = await aiChat.getThread(threadId);
        
        if (response && response.messages && controllerHydrateRef.current) {
          // Transform API messages to component format
          // Messages from API are in chronological order (oldest first, newest last)
          const hydratedMessages = response.messages.map((msg, index) => {
            if (msg.role === 'user') {
              return {
                id: msg.id || `u-hist-${index}`,
                role: 'user',
                text: msg.content,
              };
            } else {
              // agent/assistant message - API returns 'assistant' role
              const agentMsg = {
                id: msg.id || `a-hist-${index}`,
                role: 'agent',
                text: msg.content,
                fromHistory: true,  // Mark as historical - hides rating & timeline
              };
              // Include dataDisplay definitions if present (for rendering charts/tables)
              if (msg.dataDisplays) {
                agentMsg.dataDisplays = msg.dataDisplays;
              }
              return agentMsg;
            }
          });
          
          // Hydrate the chat with historical messages
          controllerHydrateRef.current(hydratedMessages, threadId);
          setCurrentThreadId(threadId);
        }
      } catch (error) {
        console.error('Error fetching thread history:', error);
        setHydrationError(true);
        errorToast({ message: HYDRATION_ERROR_MESSAGE });
      } finally {
        setIsHydrating(false);
      }
    };

    // Fetch if we have a threadId in the URL and it's different from previous
    // OR if we're on initial mount with a threadId
    if (urlThreadId && (urlThreadId !== prevUrlThreadIdRef.current || !prevUrlThreadIdRef.current)) {
      prevUrlThreadIdRef.current = urlThreadId;
      fetchThreadHistory(urlThreadId);
    } else if (!urlThreadId && prevUrlThreadIdRef.current) {
      // Navigated back to /ai-chat (no threadId) - reset state like "New" button
      prevUrlThreadIdRef.current = null;
      const newThreadId = uuidv4();
      setCurrentThreadId(newThreadId);
      setSuppressIntro(false);
      setHydrationError(false);
      
      if (controllerResetRef.current) {
        controllerResetRef.current();
      }
      
      // Refresh thread list
      dispatch(fetchChatThreads());
    }
  }, [urlThreadId, dispatch]);

  // Refresh thread list when navigating away from a thread
  useEffect(() => {
    return () => {
      // On unmount or when urlThreadId changes away, refresh threads
      if (urlThreadId) {
        dispatch(fetchChatThreads());
      }
    };
  }, [dispatch, urlThreadId]);

  // Handle New button - generate new threadId, reset chat, navigate to /ai-chat
  const handleNew = useCallback(() => {
    // If we are on a thread route, navigating back to /ai-chat will trigger
    // the URL-change effect which resets the chat and generates a new threadId.
    if (urlThreadId) {
      history.push(ROUTES.AI_CHAT);
      return;
    }

    const newThreadId = uuidv4();
    setCurrentThreadId(newThreadId);
    setSuppressIntro(false);
    setHydrationError(false);
    
    if (controllerResetRef.current) {
      controllerResetRef.current();
    }
    
    // Refresh thread list
    dispatch(fetchChatThreads());
  }, [dispatch, history, urlThreadId]);

  const handleDeleteThread = useCallback(async () => {
    try {
      await aiChat.deleteThread(currentThreadId);
    } catch (e) {
      const status = e && e.response && e.response.status;
      // If the thread doesn't exist server-side, treat as success and still reset UI.
      if (status !== 404) {
        errorToast({
          message: 'Unable to delete conversation. Please try again later.',
        });
        return;
      }
    }

    // Success: refresh thread menu
    dispatch(fetchChatThreads());

    // Reset session/messages
    if (urlThreadId) {
      // URL-change effect will reset the chat
      history.push(ROUTES.AI_CHAT);
    } else {
      // Already on /ai-chat; must explicitly reset
      handleNew();
    }
  }, [currentThreadId, dispatch, handleNew, history, urlThreadId]);

  const handleError = useCallback((error) => {
    // Errors handled via toast in ChatWithControls
  }, []);

  const handleThreadIdChange = useCallback((id) => {
    // Thread ID managed by parent
  }, []);

  const threadTitle = useMemo(() => {
    const threads = Array.isArray(chatThreads) ? chatThreads : [];
    const id = urlThreadId || currentThreadId;
    const match = threads.find((t) => t && t.id === id);
    return match && match.title ? match.title : undefined;
  }, [chatThreads, urlThreadId, currentThreadId]);

  const handleRenameSave = useCallback(
    async (newTitle) => {
      const id = urlThreadId || currentThreadId;
      await dispatch(updateChatThread(id, { title: newTitle }));
    },
    [dispatch, urlThreadId, currentThreadId],
  );

  const threadTitleRef = useRef(threadTitle);
  threadTitleRef.current = threadTitle;

  const handleStreamClose = useCallback(() => {
    if (threadTitleRef.current) return; // title known — nothing to do
    const id = urlThreadId || currentThreadId;
    dispatch(refreshThreadsForTitle(id));
  }, [dispatch, urlThreadId, currentThreadId]);

  const orgContextValue = useMemo(() => ({ organisationName }), [organisationName]);

  return (
    <OrganisationContext.Provider value={orgContextValue}>
      <Layout>
        <Mobile>
        <TopBarLayout>
          <Row>
            <ChatTabs chatThreads={chatThreads} history={history} />
          </Row>
        </TopBarLayout>
      </Mobile>
      <Wrapper>
        <StretchPanel>
          <AIChatWrapper
            threadId={currentThreadId}
            onError={handleError}
            onThreadIdChange={handleThreadIdChange}
            suppressIntroMessage={suppressIntro}
            staffDetectionDisabled={staffDetectionDisabled}
            demographicsDisabled={demographicsDisabled}
            availableMetricGroups={availableMetricGroups}
            onStreamClose={handleStreamClose}
          >
            {/* Keyed by thread so switching chats remounts the bar, discarding
                any in-progress title edit (edits cancel silently on navigation) */}
            <TopBar
              key={urlThreadId || currentThreadId}
              onNew={handleNew}
              onDelete={handleDeleteThread}
              threadTitle={threadTitle}
              onRenameSave={handleRenameSave}
            />
            <BodyFlex>
              <ChatAreaWrapper>
                {isHydrating && (
                  <LoadingOverlay>
                    <Spinner size={50} />
                  </LoadingOverlay>
                )}
                <ChatArea>
                  <ChatWithControls
                    inputRef={inputRef}
                    controllerResetRef={controllerResetRef}
                    controllerHydrateRef={controllerHydrateRef}
                    onGetThreadId={handleThreadIdChange}
                    isHydrating={isHydrating}
                    hydrationError={hydrationError}
                    initialPrompt={!urlThreadId ? initialPrompt : undefined}
                    onConsumeInitialPrompt={consumeInitialPrompt}
                  />
                </ChatArea>
              </ChatAreaWrapper>
            </BodyFlex>
          </AIChatWrapper>
        </StretchPanel>
      </Wrapper>
    </Layout>
    </OrganisationContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  chatThreads: Selectors.getSortedChatThreads(state),
  organisationName: Selectors.getOrganisationName(state),
  staffDetectionDisabled: !Selectors.userHasFeature(ORG_HAS_STAFF_DETECTION, state),
  demographicsDisabled: Selectors.userHasFeature(DISABLE_AGE_GENDER, state),
  availableMetricGroups: Selectors.getAvailableMetricGroups(state),
});

export default connect(mapStateToProps)(AIChat);
